import { Fragment, ReactNode } from 'react';
import { ChevronRight } from 'lucide-react';

export interface BreadcrumbItem {
  label: ReactNode;
  /** Jeśli podany - renderujemy <a>. Inaczej span (current page). */
  href?: string;
  onClick?: () => void;
  icon?: ReactNode;
}

export interface BreadcrumbsProps {
  items: BreadcrumbItem[];
  className?: string;
  separator?: ReactNode;
}

export function Breadcrumbs({ items, className, separator }: BreadcrumbsProps) {
  const sep = separator ?? <ChevronRight className="w-3.5 h-3.5 text-text-disabled shrink-0" aria-hidden="true" />;

  return (
    <nav aria-label="Breadcrumb" className={className}>
      <ol className="flex items-center gap-1.5 flex-wrap text-sm">
        {items.map((item, idx) => {
          const isLast = idx === items.length - 1;
          const interactive = !isLast && (item.href || item.onClick);
          const baseCls = 'inline-flex items-center gap-1.5 transition-colors duration-fast';
          const textCls = isLast
            ? 'text-text-primary font-medium'
            : interactive
              ? 'text-text-muted hover:text-text-primary'
              : 'text-text-muted';

          const content = (
            <>
              {item.icon && (
                <span className="inline-flex items-center justify-center w-3.5 h-3.5 shrink-0 [&>svg]:w-full [&>svg]:h-full" aria-hidden="true">
                  {item.icon}
                </span>
              )}
              <span className="truncate">{item.label}</span>
            </>
          );

          return (
            <Fragment key={idx}>
              <li className="min-w-0">
                {interactive ? (
                  item.href ? (
                    <a href={item.href} className={`${baseCls} ${textCls}`}>{content}</a>
                  ) : (
                    <button type="button" onClick={item.onClick} className={`${baseCls} ${textCls}`}>
                      {content}
                    </button>
                  )
                ) : (
                  <span aria-current={isLast ? 'page' : undefined} className={`${baseCls} ${textCls}`}>
                    {content}
                  </span>
                )}
              </li>
              {!isLast && <li aria-hidden="true">{sep}</li>}
            </Fragment>
          );
        })}
      </ol>
    </nav>
  );
}
